/**
 * CategoriesPage — Catálogo · Categorías de productos (jerárquicas).
 *
 * Las categorías marcadas como "de referencia" (is_reference) agrupan productos
 * no inventariables usados solo para relaciones de compatibilidad (ej. Vehículos).
 */
import { useState, useEffect } from 'react'
import {
  Plus,
  PencilLine,
  Trash2,
  Loader2,
  FolderTree,
  FolderOpen,
  Layers,
} from 'lucide-react'
import { useForm } from 'react-hook-form'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/Skeleton'
import { DataTable } from '@/components/ds/DataTable'
import { KebabMenu } from '@/components/KebabMenu'
import { ConfirmModal } from '@/components/ConfirmModal'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { ToastContainer, useToast } from '@/components/Toast'
import { usePermission } from '@/hooks/usePermission'
import { extractFieldErrors } from '@/lib/errorUtils'
import {
  useProductCategories, useCreateProductCategory, useUpdateProductCategory,
  useDeleteProductCategory, type ProductCategory,
} from '@/modules/sales/hooks/useCatalog'
import { useCostCenters } from '@/modules/accounting/hooks/useAccounting'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { Modal } from '@/components/ds/Modal'

type FormValues = {
  name: string
  parent: string
  description: string
  default_cost_center: string
  is_reference: boolean
  is_active: boolean
}

function CategoryModal({ open, onClose, onSubmit, isPending, initial, categories, serverErrors }: {
  open: boolean
  onClose: () => void
  onSubmit: (data: Partial<ProductCategory>) => void
  isPending: boolean
  initial?: ProductCategory | null
  categories: ProductCategory[]
  serverErrors?: Record<string, string>
}) {
  const { register, handleSubmit, reset, setError, formState: { errors } } = useForm<FormValues>()
  const { data: ccData } = useCostCenters({ is_active: 'true' })
  const costCenters = ccData?.cost_centers ?? []

  useEffect(() => {
    if (open) {
      reset({
        name: initial?.name ?? '',
        parent: initial?.parent ?? '',
        description: initial?.description ?? '',
        default_cost_center: initial?.default_cost_center ?? '',
        is_reference: initial?.is_reference ?? false,
        is_active: initial?.is_active ?? true,
      })
    }
  }, [open, initial, reset])

  useEffect(() => {
    if (serverErrors) {
      Object.entries(serverErrors).forEach(([field, message]) =>
        setError(field as keyof FormValues, { type: 'server', message }))
    }
  }, [serverErrors, setError])

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  // No permitir que una categoría sea su propio padre.
  const parentOptions = categories.filter(c => c.id !== initial?.id)

  const wrapped = (v: FormValues) => onSubmit({
    name: v.name,
    parent: v.parent || null,
    description: v.description,
    default_cost_center: v.default_cost_center || null,
    is_reference: v.is_reference,
    is_active: v.is_active,
  })

  return (
    <Modal open onClose={onClose} title={initial?.id ? 'Editar categoría' : 'Nueva categoría'} size="lg">
      <form onSubmit={handleSubmit(wrapped)} className="space-y-3">
        <TextField
          dense
          label="Nombre"
          required
          placeholder="Ej: Frenos, Filtros, Vehículos…"
          inputProps={{ ...register('name', { required: 'El nombre es obligatorio.' }), autoFocus: true }}
          error={errors.name?.message}
        />

        <SelectField
          dense
          label="Categoría padre (opcional)"
          selectProps={register('parent')}
          error={errors.parent?.message}
        >
          <option value="">— Categoría raíz —</option>
          {parentOptions.map(c => (
          <option key={c.id} value={c.id}>{c.parent_name ? `${c.parent_name} › ${c.name}` : c.name}</option>
          ))}
        </SelectField>

        <TextField
          dense
          label="Descripción"
          multiline
          rows={2}
          placeholder="Descripción opcional…"
          inputProps={register('description')}
        />

        <SelectField
          dense
          label="Centro de costo por defecto (opcional)"
          selectProps={register('default_cost_center')}
          hint="Se aplica a los productos de la categoría que no definan uno propio. Definirlo acá evita repetirlo producto por producto."
        >
          <option value="">— Sin centro por defecto —</option>
          {costCenters.map(cc => (
          <option key={cc.id} value={cc.id}>{cc.code} · {cc.name}</option>
          ))}
        </SelectField>

        <label className="flex items-start gap-3 cursor-pointer p-3 bg-surface-muted rounded-xl border border-divider">
          <input {...register('is_reference')} type="checkbox"
            className="mt-0.5 w-4 h-4 rounded text-primary border-divider-strong focus:ring-primary" />
          <div>
            <p className="text-sm font-medium text-content">Categoría de referencia</p>
            <p className="text-xs text-content-subtle">
              Productos no inventariables usados solo para compatibilidad (ej. "Vehículo": Toyota Hilux 2018).
            </p>
          </div>
        </label>

        {initial?.id && (
          <label className="flex items-center gap-3 cursor-pointer">
            <input {...register('is_active')} type="checkbox"
              className="w-4 h-4 rounded text-primary border-divider-strong focus:ring-primary" />
            <span className="text-sm text-content">Activa</span>
          </label>
        )}

        <div className="flex justify-end gap-3 pt-2 border-t border-divider">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
            Cancelar
          </button>
          <button type="submit" disabled={isPending}
            className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors">
            {isPending && <Loader2 size={14} className="animate-spin" />}
            {initial?.id ? 'Guardar cambios' : 'Crear categoría'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export function CategoriesPage() {
  const toast = useToast()
  const { can } = usePermission()
  const canEdit = can('sales.products', 'edit')
  const canCreate = can('sales.products', 'create')

  const { data, isLoading } = useProductCategories({ active: 'all' })
  const categories = data?.categories ?? []

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<ProductCategory | null>(null)
  const [serverErrors, setServerErrors] = useState<Record<string, string>>()
  const [deleting, setDeleting] = useState<ProductCategory | null>(null)

  const createMut = useCreateProductCategory()
  const updateMut = useUpdateProductCategory(editing?.id ?? '')
  const deleteMut = useDeleteProductCategory()

  const handleSubmit = (payload: Partial<ProductCategory>) => {
    setServerErrors(undefined)
    const onError = (e: unknown) => {
      const fields = extractFieldErrors(e)
      if (fields) setServerErrors(fields)
      else toast.error('No se pudo guardar la categoría. ¿Nombre duplicado en el mismo nivel?')
    }
    if (editing?.id) {
      updateMut.mutate(payload, {
        onSuccess: () => { setModalOpen(false); toast.success('Categoría actualizada.') },
        onError,
      })
    } else {
      createMut.mutate(payload, {
        onSuccess: () => { setModalOpen(false); toast.success('Categoría creada.') },
        onError,
      })
    }
  }

  const handleDelete = () => {
    if (!deleting) return
    deleteMut.mutate(deleting.id, {
      onSuccess: () => { setDeleting(null); toast.success('Categoría eliminada.') },
      onError: () => {
        setDeleting(null)
        toast.error('No se puede eliminar: la categoría tiene productos o subcategorías.')
      },
    })
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <PageHeader
        title="Categorías"
        subtitle={`${categories.length} ${categories.length === 1 ? 'categoría' : 'categorías'} en el catálogo`}
        help={HELP['sales.categories']}
      >
        {canCreate && (
          <button onClick={() => { setEditing(null); setServerErrors(undefined); setModalOpen(true) }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors">
            <Plus size={16} />Nueva categoría
          </button>
        )}
      </PageHeader>

      <DataTable<typeof categories[number]>
        columns={[
          { key: 'name', header: 'Categoría', sortable: true,
            render: cat => (
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0
                  ${cat.is_reference ? 'bg-warning-surface' : 'bg-primary-50'}`}>
                  {cat.is_reference
                    ? <Layers size={14} className="text-warning-content-soft" />
                    : <FolderOpen size={14} className="text-primary" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-content">{cat.name}</p>
                  {cat.description && (
                    <p className="text-xs text-content-subtle truncate max-w-xs">{cat.description}</p>
                  )}
                </div>
              </div>
            ) },
          { key: 'parent_name', header: 'Padre', sortable: true,
            render: cat => <span className="text-content-muted">{cat.parent_name ?? '—'}</span> },
          { key: 'is_reference', header: 'Tipo', sortable: true,
            sortValue: cat => (cat.is_reference ? 'Referencia' : 'Inventariable'),
            render: cat => (cat.is_reference ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-warning-surface text-warning-content">
                Referencia
              </span>
            ) : (
              <span className="text-content-subtle">Inventariable</span>
            )) },
          { key: 'product_count', header: 'Productos', numeric: true, sortable: true,
            sortValue: cat => Number(cat.product_count) },
          { key: 'is_active', header: 'Estado', sortable: true,
            sortValue: cat => (cat.is_active ? 1 : 0),
            render: cat => (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
                ${cat.is_active ? 'bg-success-surface text-success-content' : 'bg-surface-muted text-content-muted'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${cat.is_active ? 'bg-success-solid' : 'bg-content-subtle'}`} />
                {cat.is_active ? 'Activa' : 'Inactiva'}
              </span>
            ) },
          ...(canEdit ? [{
            key: 'acciones', header: '', width: 140,
            render: (cat: typeof categories[number]) => (
              <div className="flex items-center justify-end gap-1">
                <button onClick={() => { setEditing(cat); setServerErrors(undefined); setModalOpen(true) }}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
                  <PencilLine size={11} />Editar
                </button>
                <KebabMenu items={[
                  { label: 'Eliminar', icon: <Trash2 size={14} />, onClick: () => setDeleting(cat),
                    className: 'text-danger-content-soft hover:bg-danger-surface' },
                ]} />
              </div>
            ),
          }] : []),
        ]}
        rows={categories}
        rowKey={cat => cat.id}
        minWidth={820}
        loading={isLoading}
        loadingNode={<TableSkeleton rows={6} cols={5} />}
        empty={
          <EmptyState
            icon={FolderTree}
            title="Sin categorías"
            hint="Crea familias de productos (Frenos, Filtros…) y una categoría de referencia para los vehículos."
          />
        }
        caption="Categorías del catálogo de productos"
      />

      <CategoryModal
        key={editing?.id ?? 'new'}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        isPending={editing?.id ? updateMut.isPending : createMut.isPending}
        initial={editing}
        categories={categories}
        serverErrors={serverErrors}
      />

      <ConfirmModal
        open={deleting !== null}
        message={`¿Eliminar la categoría "${deleting?.name}"? Solo se permite si no tiene productos ni subcategorías.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
        isPending={deleteMut.isPending}
        variant="danger"
      />

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}
